import { useGuides } from "./lib/useGuides";
import { Suspense, lazy, useEffect, useState } from "react";
import TopBar from "./components/TopBar";
import Sidebar from "./components/Sidebar";
// Page components are code-split: each route's code is downloaded only when that
// view is opened, so the initial load ships the chrome + the active page only.
const Home = lazy(() => import("./pages/home/Home"));
const Repositories = lazy(() => import("./pages/repositories/Repositories"));
const SystemDocs = lazy(() => import("./pages/system-docs/SystemDocs"));
const KnowledgeGraph = lazy(() => import("./pages/knowledge-graph/KnowledgeGraph"));
const DevHubOverview = lazy(() => import("./devhub/pages/DevHubOverview"));
const DevHubPublicPreview = lazy(() => import("./devhub/public/DevHubPublicPreview"));
const SystemAgents = lazy(() => import("./pages/agents/SystemAgents"));
const McpPage = lazy(() => import("./pages/mcp/McpPage"));
const GuidesPage = lazy(() => import("./pages/guides/GuidesPage"));
const SystemCommon = lazy(() => import("./pages/agents-common/SystemCommon"));
const Runtime = lazy(() => import("./pages/runtime/Runtime"));
const AgentHub = lazy(() => import("./pages/agent-hub/AgentHub"));
const Settings = lazy(() => import("./pages/settings/Settings"));
const AgentsHubOverview = lazy(() => import("./pages/agent-hub/AgentsHubOverview"));
const TasksBoard = lazy(() => import("./pages/agent-hub/TasksBoard"));
const ChatWidget = lazy(() => import("./components/ChatWidget"));
import { useHub } from "./lib/useHub";
import { useLocation, navigate } from "./lib/router";
import { parseRoute, pathFor } from "./lib/routes";
import { orgLabel } from "./lib/markdown";
import { useDevhubStructure } from "./devhub";
import { useOrgs } from "./lib/useOrgs";
import { isAppAdmin, useMe } from "./lib/useMe";
import { OrgId, View } from "./types";

// Human-readable label for the current page, handed to the chat widget so the
// agent knows what the user is looking at.
const VIEW_NAMES: Record<View, string> = {
  [View.Home]: "Home",
  [View.Hub]: "Agents Hub",
  [View.Sessions]: "Agents Hub — Sessions",
  [View.Tasks]: "Agents Hub — Tasks",
  [View.Skills]: "Agents Hub — Common",
  [View.Runtime]: "Agents Hub — Runtime",
  [View.Repos]: "Repositories",
  [View.Docs]: "System Docs",
  [View.Graph]: "Knowledge Graph",
  [View.Devhub]: "DevHub",
  [View.Preview]: "DevHub — Preview",
  [View.Agents]: "Agents",
  [View.Mcp]: "MCP",
  [View.Guides]: "Guides",
  [View.Settings]: "Settings",
};
// The DevHub views. Which orgs actually expose DevHub is data-driven (the set of
// orgs that have a corpus, from /api/devhub/structure) — not hardcoded here.
const DEVHUB_VIEWS: View[] = [View.Devhub];
const ORG_SCOPED: View[] = [View.Repos, View.Docs, View.Graph, View.Devhub, View.Guides, View.Agents, View.Mcp];
// Knowledge-base overviews restricted to Omni Hub admins (see the me/admin gate).
const ADMIN_VIEWS: View[] = [View.Repos, View.Graph, View.Agents, View.Mcp, View.Runtime];
function pageLabel(view: View, org: OrgId): string {
  const base = VIEW_NAMES[view];
  return ORG_SCOPED.includes(view) ? `${base} — ${orgLabel(org)}` : base;
}

export default function App() {
  const { pathname } = useLocation();
  // The public docs preview is a standalone, brand-styled reader surface opened
  // in a new tab — it renders with the Incode public-docs design system and none
  // of the Omni Hub admin chrome (TopBar / Sidebar / ChatWidget). Short-circuit
  // BEFORE mounting HubApp: the hub runtime (useHub / useDevhubStructure) opens
  // the SSE connection, fetches agents/presence, and heartbeats presence — none
  // of which the reader tab should do. Hooks can't be conditional, so the hub
  // hooks live in HubApp, which simply isn't mounted on /preview.
  if (parseRoute(pathname).view === View.Preview) {
    return <Suspense fallback={null}><DevHubPublicPreview /></Suspense>;
  }
  return <HubApp />;
}

function HubApp() {
  const { pathname, query } = useLocation();
  const route = parseRoute(pathname);
  const view = route.view;
  // The hub owns the app-wide SSE connection plus the session/task board. The
  // board is loaded eagerly (once) because the global sidebar shows live
  // session/task counts on every view — not just Home / the Agents Hub.
  const hub = useHub();
  // Which orgs expose DevHub, derived from the corpus (orgs that have a corpus).
  const { orgs: devhubOrgs, loaded: devhubLoaded } = useDevhubStructure();
  const orgHasDevhub = (o: OrgId | string) => devhubOrgs.includes(o);
  const firstDevhubOrg = (devhubOrgs[0] as OrgId | undefined) ?? OrgId.Incode;
  // Which orgs to showcase in the left menu — those whose knowledge base is
  // available (repo token configured; every org in local dev).
  const { orgs: showcasedOrgs, loaded: orgsLoaded } = useOrgs();
  // The knowledge base's Repositories, Knowledge Graph and Agents overviews, and
  // the agent Runtime, are Omni Hub admin-only (server-enforced — the API 403s
  // non-admins). Until /api/me
  // resolves we render neither the page nor a redirect, so admins don't bounce
  // off a deep link and non-admins never fire the forbidden fetches.
  const me = useMe();
  const admin = isAppAdmin(me);
  useEffect(() => {
    if (me && !admin && ADMIN_VIEWS.includes(view)) navigate("/", { replace: true });
  }, [me, admin, view]);

  // Org for the selector: from the URL when on an org-scoped page, otherwise the
  // last org the user was viewing (global pages aren't org-specific).
  const [rememberedOrg, setRememberedOrg] = useState<OrgId>(route.org ?? OrgId.Incode);
  const org = route.org ?? rememberedOrg;
  // The org's internal guide collections — the rail's Guides entry and the page.
  const guides = useGuides(org);
  useEffect(() => {
    if (route.org && route.org !== rememberedOrg) setRememberedOrg(route.org);
  }, [route.org, rememberedOrg]);

  // DevHub exists only for orgs with a corpus; redirect to one that has it if
  // reached for an org that doesn't (once the structure has loaded).
  useEffect(() => {
    if (devhubLoaded && DEVHUB_VIEWS.includes(view) && !orgHasDevhub(org)) {
      navigate(pathFor(view, firstDevhubOrg), { replace: true });
    }
  }, [view, org, devhubLoaded, devhubOrgs]);

  // Only showcased orgs are navigable; if the current org isn't one (e.g. a
  // deep-link to an org whose token is unset), move to the first showcased org —
  // redirecting the URL on an org-scoped page, else just updating the remembered org.
  useEffect(() => {
    if (!orgsLoaded || showcasedOrgs.length === 0 || showcasedOrgs.includes(org)) return;
    const fallback = showcasedOrgs[0];
    if (route.org && ORG_SCOPED.includes(view)) navigate(pathFor(view, fallback), { replace: true });
    else setRememberedOrg(fallback);
  }, [orgsLoaded, showcasedOrgs, org, view, route.org]);

  // Deep-links carried as query params (set by repo links / dashboard cards),
  // consumed once by the page then stripped from the URL.
  const docOpenPath = view === View.Docs ? query.get("doc") ?? undefined : undefined;
  const agentsOpenRepo = view === View.Agents ? query.get("repo") ?? undefined : undefined;
  // A node id ("service/omni-api"), a slug, or any alias ("user-service") — the
  // API resolves all three, so a link from anywhere can name the node loosely.
  const graphOpenRef = view === View.Graph ? query.get("node") ?? undefined : undefined;
  // Shareable session deep-link (?s=<id>): opens that session on the Sessions
  // board. Unlike the doc/repo params it's kept in the URL (not consumed), so the
  // link stays copyable and the URL always reflects the open session.
  const sessionOpenId = view === View.Sessions ? query.get("s") ?? undefined : undefined;
  // Task deep-link (?t=<id>): open that task's detail on the Tasks board (e.g. a
  // task-done notification click).
  const taskOpenId = view === View.Tasks ? query.get("t") ?? undefined : undefined;

  function goToView(v: View) {
    navigate(pathFor(v, org));
  }

  function selectOrg(next: OrgId) {
    setRememberedOrg(next);
    // On an org-scoped page, switch org in place. DevHub falls back to Home for
    // orgs without a corpus so the user isn't stranded on an empty view.
    if (route.org) {
      const blocked = devhubLoaded && DEVHUB_VIEWS.includes(view) && !orgHasDevhub(next);
      navigate(pathFor(blocked ? View.Home : view, next));
    }
  }


  function openRepoDoc(targetOrg: string, docPath: string) {
    navigate(`/${targetOrg}/docs?doc=${encodeURIComponent(docPath)}`);
  }

  function openAgent(targetOrg: OrgId, repo: string) {
    navigate(`/${targetOrg}/agents${repo ? `?repo=${encodeURIComponent(repo)}` : ""}`);
  }

  return (
    <>
      <TopBar hub={hub} />

      <div className="main">
        <Sidebar
          org={org}
          orgs={showcasedOrgs}
          view={view}
          hub={hub}
          guides={guides}
          onOrg={selectOrg}
          onView={goToView}
          /* Loaded, but this user's tenant scope is empty (no owned agents and no
             admin-granted org membership). The sidebar shows an access note in place
             of the System Knowledge Base control for non-admins. */
          noOrgs={orgsLoaded && showcasedOrgs.length === 0}
        />

        <main className="content-area">
         <Suspense fallback={<div className="content-loading" />}>
          {view === View.Home && (
            <Home
              hub={hub}
              onOpenAgent={openAgent}
              onOpenDoc={openRepoDoc}
              onGoView={goToView}
              /* Only the orgs this user can access — Home renders one board column
                 each and hides the "By org" section entirely when granted none. */
              orgs={showcasedOrgs}
            />
          )}
          {view === View.Repos && admin && <Repositories org={org} onOpenDoc={openRepoDoc} />}
          {view === View.Docs && (
            <SystemDocs
              org={org}
              openPath={docOpenPath}
              onConsumeOpenPath={() => navigate(`/${org}/docs`, { replace: true })}
            />
          )}
          {/* The Atlas: a knowledge base whose pages ARE the graph nodes. One page
              — nav, reader, and neighborhood map are panes inside it, and the open
              node is the ?node= query (an id, a slug, or any alias). */}
          {view === View.Graph && admin && (
            <KnowledgeGraph
              org={org}
              openRef={graphOpenRef}
              onConsumeOpenRef={() => navigate(`/${org}/graph`, { replace: true })}
            />
          )}
          {/* The whole DevHub surface — Docs / API Reference / pinned-section tabs,
              per-page reading + inline edit — is one page; the active tab is the URL
              segment (/<org>/devhub/<tab>). */}
          {view === View.Devhub && orgHasDevhub(org) && <DevHubOverview org={org} />}
          {view === View.Agents && admin && (
            <SystemAgents
              org={org}
              openRepo={agentsOpenRepo}
              onConsumeOpenRepo={() => navigate(`/${org}/agents`, { replace: true })}
            />
          )}
          {view === View.Mcp && admin && (
            <McpPage
              org={org}
              selected={query.get("name") ?? undefined}
              onSelect={(name) => navigate(name ? `/${org}/mcp?name=${encodeURIComponent(name)}` : `/${org}/mcp`)}
            />
          )}
          {view === View.Guides && <GuidesPage org={org} guides={guides} />}
          {view === View.Hub && <AgentsHubOverview hub={hub} onGoView={goToView} />}
          {view === View.Skills && <SystemCommon />}
          {view === View.Runtime && admin && <Runtime />}
          {view === View.Sessions && <AgentHub hub={hub} openSessionId={sessionOpenId} />}
          {view === View.Tasks && <TasksBoard hub={hub} openTaskId={taskOpenId} />}
          {view === View.Settings && <Settings />}
         </Suspense>
        </main>
      </div>

      <Suspense fallback={null}>
        <ChatWidget agents={hub.agents} page={{ label: pageLabel(view, org), path: pathname }} createTask={hub.createTask} />
      </Suspense>
    </>
  );
}
